import React, { useMemo, useState } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa6";

export const useTableSortHeader = (data = [], valueExtraction = {}) => {
    const [order, setOrder] = useState({field: null, direction: null});

    const handleSort = (field) => {
        setOrder(prev => {
            if (prev.field !== field) return { field, direction: 'asc' };
            if (prev.direction === 'asc') return { field, direction: 'desc' };
            return { field: null, direction: null };
        });
    };

    const parseDate = (value) => {
        if (value == null || value === "") return null;

        if (value instanceof Date) {
            const time = value.getTime();
            return Number.isNaN(time) ? null : time;
        }

        let str = String(value).trim().replace(/\\\//g, "/");
        if (!str) return null;

        const buildDate = (y, m, d, h = "00", min = "00", s = "00") => {
            const date = new Date(+y, +m - 1, +d, +h, +min, +s);
            return Number.isNaN(date.getTime()) ? null : date.getTime();
        };

        let match = str.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
        if (match) {
            const [, d, m, y, h, min, s] = match;
            return buildDate(y, m, d, h, min, s);
        }

        match = str.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
        if (match) {
            const [, y, m, d, h, min, s] = match;
            return buildDate(y, m, d, h, min, s);
        }

        const nativeTime = new Date(str).getTime();
        return Number.isNaN(nativeTime) ? null : nativeTime;
    };

    const parseDuration = (value) => {
        if (value == null || value === "") return null;

        const match = String(value).trim().match(/^(?:(\d+)\s*sem\s*)?(?:(\d+)\s*d\s*)?(?:(\d+)\s*h\s*)?(?:(\d+)\s*min\s*)?(?:(\d+)\s*s\s*)?$/i);
        if (!match) return null;

        const [, weeks, days, hours, minutes, seconds] = match;
        if ([weeks, days, hours, minutes, seconds].every(item => item === undefined)) return null;

        return (Number(weeks ?? 0) * 604800) + (Number(days ?? 0) * 86400) + (Number(hours ?? 0) * 3600) + (Number(minutes ?? 0) * 60) + Number(seconds ?? 0);
    };

    const parseNumberYear = (value) => {
        if (value == null || value === "") return null;

        const match = String(value).trim().replace(/\\\//g, "/").match(/^(\d+)\/(\d{4})$/);
        if (!match) return null;

        return {
            number: parseInt(match[1], 10),
            year: parseInt(match[2], 10),
        };
    };

    const compareNumberYear = (valueA, valueB) => {
        if (valueA.year !== valueB.year) return valueA.year - valueB.year;
        return valueA.number - valueB.number;
    };

    const sortedData = useMemo(() => {
        if (!data) return [];
        if (!order.field) return data;

        return [...data].sort((a, b) => {
            const valueExtracted = (row, field) => {
                if (valueExtraction[field]) return valueExtraction[field](row);
                return row[field];
            };

            let valA = valueExtracted(a, order.field);
            let valB = valueExtracted(b, order.field);

            if (valA == null) valA = "";
            if (valB == null) valB = "";

            const strA = String(valA).trim();
            const strB = String(valB).trim();

            const isIntA = /^-?\d+$/.test(strA);
            const isIntB = /^-?\d+$/.test(strB);

            if (isIntA && isIntB) {
                const numA = parseInt(strA, 10);
                const numB = parseInt(strB, 10);

                if (numA === 0 && numB !== 0) return 1;
                if (numB === 0 && numA !== 0) return -1;

                return order.direction === 'asc' ? numA - numB : numB - numA;
            }

            const durationA = parseDuration(valA);
            const durationB = parseDuration(valB);

            if (durationA !== null && durationB !== null) return order.direction === 'asc' ? durationA - durationB : durationB - durationA;

            const numberYearA = parseNumberYear(valA);
            const numberYearB = parseNumberYear(valB);

            if (numberYearA !== null && numberYearB !== null) {
                const comparison = compareNumberYear(numberYearA, numberYearB);
                return order.direction === 'asc' ? comparison : -comparison;
            }

            const dateA = parseDate(valA);
            const dateB = parseDate(valB);

            if (dateA !== null && dateB !== null) return order.direction === 'asc' ? dateA - dateB : dateB - dateA;

            valA = String(valA).toLowerCase();
            valB = String(valB).toLowerCase();

            if (valA > valB) return order.direction === 'asc' ? 1 : -1;
            if (valA < valB) return order.direction === 'asc' ? -1 : 1;
            return 0;
        });
    }, [data, order, valueExtraction]);

    const getIcon = (order, field) => {
        if (order.field !== field) return null;
        return order.direction === 'asc' ? <FaChevronUp className="ml-1 inline"/> : <FaChevronDown className="ml-1 inline transform translate-y-[-2.5px]"/>;
    }

    return { order, handleSort, sortedData, getIcon };
};

export const tableSortHeader = useTableSortHeader;